import { useEffect, useRef, useState } from "react";

import { ChevronDown, Monitor, Moon, Sun } from "lucide-react";

import { useTheme } from "@/shared/hooks/useTheme";

/**
 * Props for the ThemeToggle component
 */
interface ThemeToggleProps {
  /** Additional CSS classes */
  className?: string;
  /** Size variant for the toggle */
  size?: "sm" | "md" | "lg";
}

/**
 * Theme toggle component with dropdown for theme selection
 * @param props - ThemeToggleProps
 * @returns JSX.Element
 */
export function ThemeToggle({ className = "", size = "md" }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Size classes for different variants
   */
  const sizeClasses = {
    sm: "text-sm px-2 py-1",
    md: "text-base px-3 py-2",
    lg: "text-lg px-4 py-3",
  };

  /**
   * Icon size classes
   */
  const iconSizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  /**
   * Theme options configuration
   */
  const themeOptions = [
    {
      value: "light" as const,
      label: "Light",
      icon: Sun,
      description: "Light theme",
    },
    {
      value: "dark" as const,
      label: "Dark",
      icon: Moon,
      description: "Dark theme",
    },
    {
      value: "system" as const,
      label: "System",
      icon: Monitor,
      description: "Follow system preference",
    },
  ];

  /**
   * Get current theme icon
   */
  const getCurrentThemeIcon = () => {
    const option = themeOptions.find((opt) => opt.value === theme);
    return option?.icon || Monitor;
  };

  /**
   * Handle theme selection
   */
  const handleThemeSelect = (selectedTheme: "light" | "dark" | "system") => {
    setTheme(selectedTheme);
    setIsOpen(false);
  };

  /**
   * Handle click outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * Handle escape key to close dropdown
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const CurrentIcon = getCurrentThemeIcon();

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 rounded-lg border border-gray-200
          bg-white px-3 py-2 text-sm font-medium text-gray-700
          shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2
          focus:ring-primary-500 focus:ring-offset-2
          dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200
          dark:hover:bg-gray-700 dark:focus:ring-primary-400
          transition-colors duration-200
          ${sizeClasses[size]}
        `}
        aria-label="Toggle theme"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <CurrentIcon className={iconSizeClasses[size]} />
        <span className="hidden sm:inline">
          {themeOptions.find((opt) => opt.value === theme)?.label}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
          {themeOptions.map((option) => {
            const OptionIcon = option.icon;
            const isActive = theme === option.value;

            return (
              <button
                key={option.value}
                onClick={() => handleThemeSelect(option.value)}
                className={`
                  flex w-full items-center gap-3 px-4 py-2 text-sm
                  transition-colors duration-200
                  ${
                    isActive
                      ? "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                      : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                  }
                `}
                role="menuitem"
              >
                <OptionIcon className="w-4 h-4" />
                <div className="flex flex-col items-start">
                  <span className="font-medium">{option.label}</span>
                  <span
                    className={`text-xs ${
                      isActive
                        ? "text-primary-600 dark:text-primary-400"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {option.description}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
